// Décision pure du garde de rôle __root.tsx — extraite pour rester testable
// sans monter de composant ni toucher le réseau (voir role-gate.spec.ts).
//
// Contexte : login.tsx vérifie déjà le rôle au moment de la connexion
// interactive (fetchRole + signOut si non-poseur). Le trou qu'il ne couvre
// pas : une session restaurée depuis le localStorage au boot de l'app (PWA
// rouverte, onglet rechargé) sans repasser par le formulaire de connexion.
// __root.tsx doit donc revérifier le rôle à chaque session présente, via
// useUserProfile (qui sert le profil en cache s'il existe, sinon retente un
// fetch réseau — voir auth.tsx setCachedProfile/useUserProfile).
import type { UserRole } from "@relais/contracts";

export type ProfileStatus = "loading" | "error" | "ready";

export type RoleGateDecision = "checking" | "allowed" | "denied";

// Reflet du `ConnectionState` d'offline-context.tsx, dupliqué ici pour que
// cette fonction reste pure et sans dépendance vers le contexte React :
// "offline" (navigator.onLine à false) et "server-unavailable" (en ligne
// mais la loop de sync détecte une erreur réseau/5xx) sont toutes les deux
// des situations où le réseau ne peut pas confirmer le rôle.
export type ConnectionState = "online" | "offline" | "server-unavailable";

export function decideRoleGate(input: {
  profileStatus: ProfileStatus;
  role: UserRole | null;
  connectionState: ConnectionState;
}): RoleGateDecision {
  if (input.profileStatus === "ready") {
    // Le rôle est connu (cache ou fetch réussi, peu importe la connectivité
    // actuelle) : on tranche définitivement, quelle que soit connectionState.
    return input.role === "poseur" ? "allowed" : "denied";
  }

  if (input.profileStatus === "error") {
    // Aucun profil en cache pour cette session et le fetch réseau a échoué.
    // Si le réseau ne peut pas confirmer le rôle — vraiment hors-ligne, ou
    // en ligne mais serveur injoignable (wifi captif, panne API, 5xx) — on
    // refuse de bloquer un poseur légitime faute d'évidence. Le vrai
    // contrôle d'accès reste appliqué côté API/service (voir TasksService)
    // dès la prochaine requête, et ce garde se réévalue automatiquement :
    // la loop de sync sonde la santé serveur toutes les 15s tant qu'elle le
    // croit injoignable, ce qui redéclenche un fetch via useUserProfile dès
    // le retour. C'est un choix documenté : fail-open uniquement quand il
    // n'y a strictement aucune évidence disponible.
    if (input.connectionState !== "online") return "allowed";
    // Vraiment en ligne (serveur confirmé joignable par la loop de sync)
    // mais ce fetch précis a échoué : on ne tranche pas, on continue
    // d'attendre plutôt que d'autoriser ou de bannir sur une base incertaine.
    return "checking";
  }

  // "loading" : fetch en cours ou pas encore déclenché.
  return "checking";
}
